import { Button } from '@creanizer/ui'
import { Card, CardContent, CardHeader, CardTitle } from '@creanizer/ui'
import { Badge } from '@creanizer/ui'
import { Copy } from 'lucide-react'
import type { CommunityResult } from '@creanizer/types'

interface CommunityOverviewProps {
  result: CommunityResult
  onCopy: (text: string) => void
}

export function CommunityOverview({ result, onCopy }: CommunityOverviewProps) {
  const formatOverview = () => {
    const lines: string[] = []
    
    lines.push(`【コミュニティ概要】${result.basicInfo.name}`)
    lines.push('')
    if (result.basicInfo.fullName && result.basicInfo.fullName !== result.basicInfo.name) {
      lines.push(`正式名称: ${result.basicInfo.fullName}`)
    }
    if (Array.isArray(result.basicInfo.nicknames) && result.basicInfo.nicknames.length > 0) {
      lines.push(`通称: ${result.basicInfo.nicknames.join('、')}`)
    }
    lines.push(`タイプ: ${result.basicInfo.type}`)
    lines.push(`規模: ${result.basicInfo.scale}`)
    lines.push(`人口: ${result.basicInfo.population?.toLocaleString() || '不明'}人`)
    if (result.basicInfo.foundedYear) {
      lines.push(`創立年: ${result.basicInfo.foundedYear}`)
    }
    if (result.basicInfo.motto) {
      lines.push(`モットー: ${result.basicInfo.motto}`)
    }
    if (result.basicInfo.symbol) {
      lines.push(`シンボル: ${result.basicInfo.symbol}`)
    }
    lines.push(`評判: ${result.basicInfo.reputation}`)
    lines.push('')
    lines.push(`雰囲気: ${result.basicInfo.atmosphere}`)
    lines.push('')
    lines.push(`第一印象: ${result.basicInfo.firstImpression}`)
    
    return lines.join('\n')
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>基本情報</CardTitle>
          <Button size="sm" variant="ghost" onClick={() => onCopy(formatOverview())}>
            <Copy className="h-4 w-4" />
          </Button>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* 名前と称号 */}
          <div>
            <h3 className="text-xl font-bold">{result.basicInfo.name}</h3>
            {result.basicInfo.fullName && result.basicInfo.fullName !== result.basicInfo.name && (
              <p className="text-sm text-gray-500">正式名称: {result.basicInfo.fullName}</p>
            )}
            {Array.isArray(result.basicInfo.nicknames) && result.basicInfo.nicknames.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-1">
                {result.basicInfo.nicknames.map((nickname, index) => (
                  <Badge key={index} variant="outline" className="text-xs">
                    {nickname}
                  </Badge>
                ))}
              </div>
            )}
          </div>

          {/* 基本データ */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <div>
              <p className="text-xs text-gray-500">タイプ</p>
              <p className="font-medium">{result.basicInfo.type}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500">規模</p>
              <p className="font-medium">{result.basicInfo.scale}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500">人口</p>
              <p className="font-medium">{result.basicInfo.population?.toLocaleString() || '不明'}人</p>
            </div>
            {result.basicInfo.foundedYear && (
              <div>
                <p className="text-xs text-gray-500">創立年</p>
                <p className="font-medium">{result.basicInfo.foundedYear}</p>
              </div>
            )}
            <div>
              <p className="text-xs text-gray-500">評判</p>
              <p className="font-medium">{result.basicInfo.reputation}</p>
            </div>
          </div>

          {/* モットー */}
          {result.basicInfo.motto && (
            <div className="border-l-4 border-primary pl-4 py-2 bg-gray-50">
              <p className="text-sm italic">"{result.basicInfo.motto}"</p>
            </div>
          )}

          {/* シンボル */}
          {result.basicInfo.symbol && (
            <div>
              <p className="text-xs text-gray-500 mb-1">紋章・シンボル</p>
              <p className="text-sm">{result.basicInfo.symbol}</p>
            </div>
          )}

          {/* 雰囲気 */}
          <div>
            <p className="text-xs text-gray-500 mb-1">全体的な雰囲気</p>
            <p className="text-sm">{result.basicInfo.atmosphere}</p>
          </div>

          {/* 第一印象 */}
          <div>
            <p className="text-xs text-gray-500 mb-1">訪問者の第一印象</p>
            <p className="text-sm">{result.basicInfo.firstImpression}</p>
          </div>
        </CardContent>
      </Card>

    </div>
  )
}